/**
 * The sale screen. Assembly only — the search, the cart and the sheets each own their own logic.
 *
 * **The four taps**, for a cash accessory sale:
 *   1. tap the search box and type (typing is not a tap; a scan removes even this)
 *   2. tap the product in the results — it goes straight into the cart at its retail price
 *   3. tap **Charge**
 *   4. tap **Complete** on the payment screen
 *
 * The cart never has to be opened, no quantity has to be confirmed, and no dialog interrupts.
 * With a scanner it is three: scan, Charge, Complete.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  can,
  formatKes,
  Permission,
  selectActiveShopId,
  selectUser,
  type CatalogueVariant,
  type UnitId,
} from '@pos/shared';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useCatalogueSync } from '@/entities/catalogue';
import { SearchPanel } from '@/features/catalogue-search';
import {
  BrokerPickerSheet,
  CartLineRow,
  CartPanel,
  CustomerNameSheet,
  CustomerPickerSheet,
  GenericLineRow,
  GenericLineSheet,
  QuantitySheet,
  SerialPickerSheet,
  brokerAttached,
  brokerDetached,
  cartRestored,
  customerAttached,
  customerChanged,
  customerDetached,
  floorFor,
  genericLineAdded,
  genericLineRemoved,
  isEmptyCart,
  lineAdded,
  loadCart,
  lineRemoved,
  priceOverridden,
  priceTypeChanged,
  quantityChanged,
  selectBrokerName,
  selectCanSubmit,
  selectCart,
  selectCustomerName,
  selectCustomerPhone,
  selectGenericLines,
  selectLineCount,
  selectLineWarnings,
  selectLines,
  selectPriceType,
  selectRowCount,
  selectTotalKes,
  type CartLine,
} from '@/features/sale-checkout';
import { warm } from '@/shared/lib/prefetch';
import { TWO_PANEL, useMediaQuery } from '@/shared/lib/useMediaQuery';
import { toast } from '@/shared/toast';
import { Button } from '@/shared/ui';

type Editing = { line: CartLine; mode: 'quantity' | 'price' } | null;

export function SalePage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const shopId = useAppSelector(selectActiveShopId);
  const lines = useAppSelector(selectLines);
  const genericLines = useAppSelector(selectGenericLines);
  const total = useAppSelector(selectTotalKes);
  const count = useAppSelector(selectLineCount);
  const rowCount = useAppSelector(selectRowCount);
  const priceType = useAppSelector(selectPriceType);
  const canSubmit = useAppSelector(selectCanSubmit);
  const warnings = useAppSelector(selectLineWarnings);
  const customerName = useAppSelector(selectCustomerName);
  const customerPhone = useAppSelector(selectCustomerPhone);
  const brokerName = useAppSelector(selectBrokerName);
  const user = useAppSelector(selectUser);
  const cart = useAppSelector(selectCart);

  // A sale in progress survives a reload: put back what was saved on this device, if it is this
  // person's at this shop. Only into an empty cart, so it never overwrites work on screen.
  const userId = user?.id;
  const cartIsEmpty = isEmptyCart(cart);
  useEffect(() => {
    if (userId === undefined || shopId === null || !cartIsEmpty) return;
    const saved = loadCart({ userId, shopId });
    if (saved !== null) dispatch(cartRestored(saved));
    // Once per arrival on the screen; later changes are the person's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, shopId]);

  // A Salesperson's default grants include neither. Without customer.view the Customer button
  // still shows, but opens a name-for-the-receipt sheet instead of the customer list: the list is
  // other people's phone numbers, the person at the counter is not. The broker list has no such
  // fallback — a broker is someone the owner registered, not someone typed in at the till.
  const canAttachCustomer = can(user, Permission.CustomerView);
  const canAttachBroker = can(user, Permission.BrokerView);
  // The server lets whoever may approve a lower price charge one without asking themselves.
  const mayGoBelowFloor = can(user, Permission.ApprovalDecide);

  const { sync } = useCatalogueSync(shopId);

  const [expanded, setExpanded] = useState(false);
  // Which tree to render, not which to hide: rendering the search twice put two inputs with one
  // id in the document. See `useMediaQuery`.
  const twoPanel = useMediaQuery(TWO_PANEL);
  const [editing, setEditing] = useState<Editing>(null);
  const [pickingFor, setPickingFor] = useState<CartLine | null>(null);
  const [pickingCustomer, setPickingCustomer] = useState(false);
  const [pickingBroker, setPickingBroker] = useState(false);
  const [addingGenericLine, setAddingGenericLine] = useState(false);

  const add = (variant: CatalogueVariant) => {
    dispatch(lineAdded(variant));

    // A serialized item cannot be sold without an IMEI, so the picker opens immediately rather
    // than letting the salesperson reach the payment screen and be turned back.
    if (variant.trackingMode === 'serialized') {
      setPickingFor({
        key: variant.id,
        variantId: variant.id,
        displayName: variant.displayName,
        sku: variant.sku,
        isSerialized: true,
        quantity: 1,
        unitPriceKes: variant.retailKes,
        retailKes: variant.retailKes,
        wholesaleKes: variant.wholesaleKes ?? null,
        wholesaleMinQty: variant.wholesaleMinQty ?? null,
        retailFloorKes: variant.retailFloorKes ?? null,
        wholesaleFloorKes: variant.wholesaleFloorKes ?? null,
        inventoryUnitId: null,
        serialNo: null,
        availableQuantity: variant.quantity,
        priceOverridden: false,
      });
    }
  };

  const warningFor = (key: string) => warnings.find((warning) => warning.key === key);

  const cartRows = (
    <>
      {/* Optional, and rarely used — most sales are a walk-in. Only shown once the cart is open,
          never on the four-tap fast path. */}
      <div className="mb-3 flex gap-2">
        <Button
          variant="quiet"
          size="sm"
          className="flex-1 justify-start"
          onClick={() => {
            setPickingCustomer(true);
          }}
        >
          {customerName ?? '+ Customer'}
        </Button>
        {customerName !== null && (
          <Button
            variant="ghost"
            size="sm"
            aria-label="Remove customer"
            onClick={() => dispatch(customerDetached())}
          >
            ×
          </Button>
        )}

        {canAttachBroker && (
          <>
            <Button
              variant="quiet"
              size="sm"
              className="flex-1 justify-start"
              onClick={() => {
                setPickingBroker(true);
              }}
            >
              {brokerName ?? '+ Broker'}
            </Button>
            {brokerName !== null && (
              <Button
                variant="ghost"
                size="sm"
                aria-label="Remove broker"
                onClick={() => dispatch(brokerDetached())}
              >
                ×
              </Button>
            )}
          </>
        )}
      </div>

      <ul className="flex flex-col gap-2">
        {genericLines.map((line) => (
          <li key={line.key}>
            <GenericLineRow
              line={line}
              onRemove={(target) => {
                dispatch(genericLineRemoved(target.key));
                toast.info(`${target.description} removed`);
              }}
            />
          </li>
        ))}

        {lines.map((line) => (
          <li key={line.key}>
            <CartLineRow
              line={line}
              priceType={priceType}
              warning={warningFor(line.key)}
              needsApproval={!mayGoBelowFloor && line.unitPriceKes < floorFor(line, priceType)}
              onEditQuantity={(target) => {
                setEditing({ line: target, mode: 'quantity' });
              }}
              onEditPrice={(target) => {
                setEditing({ line: target, mode: 'price' });
              }}
              onPickSerial={setPickingFor}
              onRemove={(target) => {
                dispatch(lineRemoved(target.key));
                toast.info(`${target.displayName} removed`);
              }}
            />
          </li>
        ))}
      </ul>
    </>
  );

  const panelProps = {
    rowCount,
    itemNames: [
      ...genericLines.map((line) => line.description),
      ...lines.map((line) => line.displayName),
    ],
    totalKes: total,
    count,
    priceType,
    canSubmit,
    onChangePriceType: (type: 'retail' | 'wholesale') => dispatch(priceTypeChanged(type)),
    onCharge: () => void navigate('/payment'),
    onWarmPayment: () => {
      warm('payment');
    },
  };

  const takenUnitIds = lines
    .map((line) => line.inventoryUnitId)
    .filter((id): id is UnitId => id !== null);

  const search = (
    <SearchPanel
      onPick={add}
      onPickUnit={(variant, unit) => {
        // The exact handset, so no picker: the IMEI was the search.
        dispatch(lineAdded(variant, unit));
      }}
      takenUnitIds={takenUnitIds}
      onRetrySync={() => void sync()}
    />
  );

  return (
    <div className="flex h-full flex-col md:flex-row">
      {/* ── Left: search. On a phone this column is also the cart, opened from the bar. ─── */}
      <section className="flex min-h-0 flex-1 flex-col md:basis-[60%] md:border-r md:border-line-subtle">
        {/* Reachable from either state, since a misc. line has to be addable with nothing else in
            the cart yet. Small and out of the way: a rare action beside the four-tap path. */}
        <div className="flex justify-end px-4 pt-2">
          <Button
            data-tour="other-item"
            variant="ghost"
            size="sm"
            onClick={() => {
              setAddingGenericLine(true);
            }}
          >
            + Service or other item
          </Button>
        </div>

        {/*
         * Phone, cart open: lines from the top, the total bar directly under the last one, and
         * whatever space is left goes *below* the bar. `flex-none` on the list and a `flex-1`
         * spacer after the bar is what puts the empty space at the bottom; once the list is
         * taller than the space it has `min-h-0` and scrolls, the spacer collapses to nothing,
         * and the bar is pinned to the foot. Nothing is positioned — it is all flex geometry.
         *
         * Phone, cart closed — and always on the two-panel surface — this column is search. The
         * cart and the sell screen are one surface: an empty cart shows the search field and the
         * prompt, not a blank.
         */}
        {!twoPanel && expanded && rowCount > 0 ? (
          <>
            <div className="scroll-region min-h-0 flex-none overflow-y-auto px-4 pt-3">
              {cartRows}
            </div>
            <CartPanel
              {...panelProps}
              layout="bar"
              expanded
              onToggleExpanded={() => {
                setExpanded(false);
              }}
            />
            <div className="flex-1" aria-hidden />
          </>
        ) : (
          <>
            <div className="min-h-0 flex-1">{search}</div>
            {!twoPanel && (
              <CartPanel
                {...panelProps}
                layout="bar"
                expanded={false}
                onToggleExpanded={() => {
                  setExpanded(true);
                }}
              />
            )}
          </>
        )}
      </section>

      {/* ── Right, two-panel surface: the sale, always visible, total and Charge at its foot. ── */}
      {twoPanel && (
        <aside className="flex min-h-0 flex-col md:basis-[40%]" aria-label="Current sale">
          {rowCount === 0 ? (
            // Near the top, left-aligned, where the first line will appear — not floating in the
            // vertical middle of a tall empty panel as though the panel were the content.
            <div className="px-4 pt-5">
              <p className="text-sm font-semibold text-content-secondary">
                Nothing in the sale yet
              </p>
              <p className="mt-1 text-sm text-content-muted">
                Pick something on the left and it lands here.
              </p>
            </div>
          ) : (
            <>
              <div className="scroll-region min-h-0 flex-none overflow-y-auto px-4 pt-3">
                {cartRows}
              </div>
              <CartPanel {...panelProps} layout="foot" />
              <div className="flex-1" aria-hidden />
            </>
          )}
        </aside>
      )}

      <QuantitySheet
        open={editing !== null}
        mode={editing?.mode ?? 'quantity'}
        title={editing?.mode === 'price' ? 'Unit price' : 'Quantity'}
        initial={
          editing?.mode === 'price' ? editing.line.unitPriceKes : (editing?.line.quantity ?? 1)
        }
        validate={(value) => {
          if (editing === null) return null;

          if (editing.mode === 'quantity') {
            return value < 1 ? 'A line needs at least one unit.' : null;
          }

          return null;
        }}
        notice={(value) => {
          if (editing?.mode !== 'price' || mayGoBelowFloor) return null;

          // Said on the keypad, against the price the catalogue sync already carries — not after a
          // round trip. It is not a refusal: the lower price stays on the line and a manager
          // approves it from their own phone when the sale is charged.
          const floor = floorFor(editing.line, priceType);
          return value < floor
            ? `Lower than ${formatKes(floor)}. A manager approves it when you charge.`
            : null;
        }}
        onCommit={(value) => {
          if (editing === null) return;

          dispatch(
            editing.mode === 'price'
              ? priceOverridden({ key: editing.line.key, unitPriceKes: value })
              : quantityChanged({ key: editing.line.key, quantity: value }),
          );

          setEditing(null);
        }}
        onDismiss={() => {
          setEditing(null);
        }}
      />

      <SerialPickerSheet
        open={pickingFor !== null}
        shopId={shopId}
        variantId={pickingFor?.variantId ?? null}
        displayName={pickingFor?.displayName ?? ''}
        takenUnitIds={lines
          .map((line) => line.inventoryUnitId)
          .filter((id): id is UnitId => id !== null)}
        onPick={(unit) => {
          if (pickingFor === null) return;
          dispatch(lineAdded(toVariant(pickingFor), unit));
          dispatch(lineRemoved(pickingFor.variantId));
          setPickingFor(null);
        }}
        onDismiss={() => {
          setPickingFor(null);
        }}
      />

      <CustomerNameSheet
        open={pickingCustomer && !canAttachCustomer}
        initialName={customerName}
        initialPhone={customerPhone}
        onSave={(name, phone) => {
          dispatch(customerChanged({ name, phone }));
          setPickingCustomer(false);
        }}
        onDismiss={() => {
          setPickingCustomer(false);
        }}
      />

      <CustomerPickerSheet
        open={pickingCustomer && canAttachCustomer}
        onPick={(customer) => {
          dispatch(
            customerAttached({
              id: customer.id,
              name: customer.name,
              phone: customer.phone ?? null,
            }),
          );
          setPickingCustomer(false);
        }}
        onDismiss={() => {
          setPickingCustomer(false);
        }}
      />

      <BrokerPickerSheet
        open={pickingBroker}
        onPick={(broker) => {
          dispatch(brokerAttached({ id: broker.id, name: broker.name }));
          setPickingBroker(false);
        }}
        onDismiss={() => {
          setPickingBroker(false);
        }}
      />

      <GenericLineSheet
        open={addingGenericLine}
        onAdd={(description, quantity, unitPriceKes) => {
          dispatch(genericLineAdded(description, quantity, unitPriceKes));
          setAddingGenericLine(false);
        }}
        onDismiss={() => {
          setAddingGenericLine(false);
        }}
      />
    </div>
  );
}

/**
 * The picker hands back a unit, and the cart wants a variant plus that unit. The placeholder
 * line already carries everything a variant needs for this, so it is reshaped rather than
 * re-fetched — the alternative is a catalogue lookup for data already in hand.
 */
function toVariant(line: CartLine): CatalogueVariant {
  return {
    id: line.variantId,
    productId: '' as CatalogueVariant['productId'],
    productName: line.displayName,
    brand: null,
    sku: line.sku,
    barcode: null,
    displayName: line.displayName,
    categoryId: '',
    categoryPath: '',
    trackingMode: 'serialized',
    attributes: {},
    retailKes: line.retailKes,
    wholesaleKes: line.wholesaleKes,
    wholesaleMinQty: line.wholesaleMinQty,
    retailFloorKes: line.retailFloorKes,
    wholesaleFloorKes: line.wholesaleFloorKes,
    quantity: line.availableQuantity,
    lowStockThreshold: 0,
    isActive: true,
    updatedAt: '',
  };
}
